import { inject, Injectable } from "@angular/core";
import { patchState, signalStore, withHooks, withState } from "@ngrx/signals";
import { environment } from "apps/merchant-portal/src/environments";
import { UnleashClient } from "unleash-proxy-client";
import {
  FeatureFlags,
  featureFlagsConst,
} from "../constants/feature-flags.constants";

@Injectable({
  providedIn: "root",
})
export class FeatureFlagService {
  private client: UnleashClient;
  private initialized: Promise<void>;

  constructor() {
    this.client = new UnleashClient({
      url: environment.unleash.url,
      clientKey: environment.unleash.clientKey,
      appName: environment.unleash.appName,
      environment: environment.unleash.environment,
    });

    this.initialized = new Promise((resolve) => {
      this.client.on("ready", () => resolve());
    });

    this.client.start();
  }

  public async asyncIsEnabled(flagName: string): Promise<boolean> {
    await this.initialized;
    return this.client.isEnabled(flagName);
  }

  public isEnabled(flagName: FeatureFlags): boolean {
    return this.client.isEnabled(flagName);
  }

  public onUpdate(callback: () => void): void {
    this.client.on("update", callback);
  }

  public getAll() {
    return this.client.getAllToggles();
  }
}

export type FeatureFlagState = { [key in FeatureFlags]: boolean };

export const FeatureFlagStore = signalStore(
  { providedIn: "root" },
  withState<FeatureFlagState>(
    Object.fromEntries(
      featureFlagsConst.map((k) => [k, false]),
    ) as FeatureFlagState,
  ),
  withHooks({
    onInit: (store, featureFlagService = inject(FeatureFlagService)) => {
      featureFlagService.onUpdate(() => {
        const allFlags = featureFlagService.getAll().reduce(
          (acc, val) => {
            acc[val.name] = val.enabled;
            return acc;
          },
          {} as Record<string, boolean>,
        );
        patchState(store, () => allFlags);
      });
    },
  }),
);
